import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Background.css";
import { DEVICES } from "../../constants/breakpoints";
import AssetPickerPopup from "./AssetPickerPopup";

const Background = ({
  selectedElement,
  updateElementStyles,
  selectedDevice,
  classStylesRegistry,
  appliedStylesRegistry,
  assets,
}) => {
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [isColorInputFocused, setIsColorInputFocused] = useState(false);
  const [backgroundClip, setBackgroundClip] = useState("none");
  const [isClipDropdownOpen, setIsClipDropdownOpen] = useState(false);
  const [backgroundType, setBackgroundType] = useState("none");
  const [selectedType, setSelectedType] = useState("image");
  const [selectedImage, setSelectedImage] = useState({
    url: 'https://placehold.co/600x400/363636/FFFFFF/png?text=Choose+Image',
    name: 'placeholder.png'
  });
  const [colorAlpha, setColorAlpha] = useState(1);
  const [isRgba, setIsRgba] = useState(false);
  const [backgroundSize, setBackgroundSize] = useState("cover");
  const [customSize, setCustomSize] = useState({
    width: { value: "100", unit: "px" },
    height: { value: "100", unit: "px" }
  });
  const [isImageGradientExpanded, setIsImageGradientExpanded] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({
    image: false,
    linear: false,
    radial: false
  });
  const [backgroundPosition, setBackgroundPosition] = useState({
    left: { value: "0", unit: "%" },
    top: { value: "0", unit: "%" }
  });
  const [appliedTypes, setAppliedTypes] = useState({
    image: false,
    linear: false,
    radial: false
  });
  const [backgroundRepeat, setBackgroundRepeat] = useState("repeat");
  const [gradientColors, setGradientColors] = useState([
    { color: '#ffffff', position: 0 },
    { color: '#000000', position: 100 }
  ]);
  const [gradientAngle, setGradientAngle] = useState(90);
  const [radialGradientShape, setRadialGradientShape] = useState('circle');
  const [radialGradientPosition, setRadialGradientPosition] = useState('center');
  const [radialGradientSize, setRadialGradientSize] = useState('farthest-corner');
  const [radialGradientColors, setRadialGradientColors] = useState([
    { color: '#ffffff', position: 0 },
    { color: '#000000', position: 100 }
  ]);
  const [isAssetPickerOpen, setIsAssetPickerOpen] = useState(false);

  // Add default values for each type
  const defaultTypeValues = {
    image: {
      selectedImage: {
        url: 'https://placehold.co/600x400/363636/FFFFFF/png?text=Choose+Image',
        name: 'placeholder.png'
      },
      backgroundSize: "auto",
      customSize: {
        width: { value: "auto", unit: "px" },
        height: { value: "auto", unit: "px" }
      },
      backgroundPosition: {
        left: { value: "0", unit: "%" },
        top: { value: "0", unit: "%" }
      },
      backgroundRepeat: "repeat"
    },
    linear: {
      angle: 90,
      colors: [
        { color: '#FFFFFF', position: 0 },
        { color: '#000000', position: 100 }
      ]
    },
    radial: {
      shape: 'circle',
      size: 'farthest-corner',
      position: 'center',
      colors: [
        { color: '#FFFFFF', position: 0 },
        { color: '#000000', position: 100 }
      ]
    }
  };

  const hexToRgb = (hex) => {
    hex = hex.replace('#', '');
    
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    if (hex.length !== 6) {
      return null;
    }
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return { r, g, b };
  };
  
  const rgbaToHex = (rgba) => {
    if (rgba.startsWith('#')) return rgba;
    
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(?:0|1|0?\.\d+))?\)/);
    if (!match) return "#000000";
    
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');
    
    return `#${r}${g}${b}`;
  };
  
  const getInitialAlpha = (color) => {
    if (color && color.startsWith('rgba')) {
      const match = color.match(/rgba\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([0-9.]+)\s*\)/);
      if (match && match[1]) {
        return parseFloat(match[1]);
      }
    }
    return 1;
  };
  
  const handleAlphaChange = (event) => {
    const newAlpha = parseFloat(event.target.value);
    setColorAlpha(newAlpha);
    
    handleBackgroundColorChange(backgroundColor, newAlpha);
  };

  useEffect(() => {
    console.log(appliedTypes);
  }, [appliedTypes]);


  useEffect(() => {
    if (selectedElement) {
      const elementStyles = selectedElement.styles;
      const currentDeviceStyles = elementStyles;
      const backgroundImage = currentDeviceStyles?.backgroundImage;

      if (elementStyles?.backgroundColor) {
        setBackgroundColor(elementStyles.backgroundColor);
        
        if (elementStyles.backgroundColor.startsWith('rgba')) {
          const alpha = getInitialAlpha(elementStyles.backgroundColor);
          setColorAlpha(alpha);
          setIsRgba(true);
        } else {
          setIsRgba(false);
        }
      }
      if (elementStyles?.backgroundClip) {
        setBackgroundClip(elementStyles.backgroundClip);
      }

      if (elementStyles?.backgroundSize) {
        setBackgroundSize(elementStyles.backgroundSize);
      }
      if (elementStyles?.backgroundPosition) {
        const [left, top] = elementStyles.backgroundPosition.split(' ');
        setBackgroundPosition({
          left: { value: parseInt(left) || 0, unit: '%' },
          top: { value: parseInt(top) || 0, unit: '%' }
        });
      }
      if (elementStyles?.backgroundRepeat) {
        setBackgroundRepeat(elementStyles.backgroundRepeat);
      }

      if (backgroundImage && backgroundImage.includes('url(')) {
        try {
          const urlMatch = backgroundImage.match(/url\(['"]?(.*?)['"]?\)/);
          if (urlMatch && urlMatch[1]) {
            const imageUrl = urlMatch[1];
            
            let fileName = "background-image.png";
            if (imageUrl.includes('/')) {
              const urlParts = imageUrl.split('/');
              fileName = urlParts[urlParts.length - 1];
            }
            
            setSelectedImage({
              url: imageUrl,
              name: fileName
            });
            
            setAppliedTypes(prev => ({
              ...prev,
              image: true
            }));
          }
        } catch (error) {
          console.error("Error extracting background image URL:", error);
        }
      }

      const hasImageBackground = backgroundImage?.includes('url(');
      const hasLinearGradient = backgroundImage?.includes('linear-gradient') && 
        !backgroundImage?.match(/linear-gradient\((#[0-9a-f]{6}[0-9a-f]{2})/i);
      const hasRadialGradient = backgroundImage?.includes('radial-gradient');

      let appliedInRegistry = false;
      if (selectedElement?.className) {
        const classNames = selectedElement.className.split(" ");
        appliedInRegistry = classNames.some(className => {
          const styles = classStylesRegistry[selectedDevice].get(className);
          return styles?.backgroundImage && styles.backgroundImage !== 'none';
        });
      }

      if ((!backgroundImage || backgroundImage === 'none') && !appliedInRegistry) {
        setAppliedTypes(prev => ({
          ...prev,
          image: false,
          linear: false,
          radial: false
        }));
        setExpandedTypes(prev => ({
          ...prev,
          image: false,
          linear: false,
          radial: false
        }));
        return;
      }

      setAppliedTypes(prev => {
        const newTypes = {
          ...prev,
          image: hasImageBackground,
          linear: hasLinearGradient,
          radial: hasRadialGradient
        };

        setExpandedTypes(prev => ({
          ...prev,
          image: hasImageBackground,
          linear: hasLinearGradient,
          radial: hasRadialGradient
        }));

        return newTypes;
      });

      if (hasLinearGradient) {
        const gradientMatch = backgroundImage.match(/linear-gradient\((.*?)\)/);
        if (gradientMatch) {
          const gradientContent = gradientMatch[1];
          
          const angleMatch = gradientContent.match(/(\d+)deg/);
          if (angleMatch) {
            setGradientAngle(parseInt(angleMatch[1]));
          }

          const colorStops = gradientContent
            .replace(/^\d+deg,\s*/, '')
            .split(',')
            .map(stop => {
              const [color, position] = stop.trim().split(/\s+/);
              return {
                color: color,
                position: parseInt(position) || 0
              };
            });

          if (colorStops.length) {
            setGradientColors(colorStops);
          }
        }
      }

      if (hasRadialGradient) {
        const radialMatch = backgroundImage.match(/radial-gradient\((.*?)\)/);
        if (radialMatch) {
          const radialContent = radialMatch[1];
          
          if (radialContent.includes('circle')) {
            setRadialGradientShape('circle');
          } else if (radialContent.includes('ellipse')) {
            setRadialGradientShape('ellipse');
          }

          ['closest-side', 'closest-corner', 'farthest-side', 'farthest-corner'].forEach(size => {
            if (radialContent.includes(size)) {
              setRadialGradientSize(size);
            }
          });

          const positionMatch = radialContent.match(/at\s+([\w\s]+)/);
          if (positionMatch) {
            setRadialGradientPosition(positionMatch[1].trim());
          }

          const colorStops = radialContent
            .split('at')[1]
            .split(',')
            .slice(1)
            .map(stop => {
              const [color, position] = stop.trim().split(/\s+/);
              return {
                color: color,
                position: parseInt(position) || 0
              };
            });

          if (colorStops.length) {
            setRadialGradientColors(colorStops);
          }
        }
      }
    }
  }, [selectedElement, selectedDevice]);

  const handleBackgroundColorChange = (color, alpha = null) => {
    const finalAlpha = alpha !== null ? alpha : colorAlpha;
    
    setBackgroundColor(color);
    
    if (color.startsWith('rgba')) {
      const newColor = color.replace(/rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[0-9.]+\s*\)/, 
        `rgba($1, $2, $3, ${finalAlpha})`);
      setIsRgba(true);
      updateElementStyles(
        selectedElement.id,
        "backgroundColor",
        newColor,
        null,
        selectedDevice
      );
    } else {
      const rgb = hexToRgb(color);
      if (rgb) {
        const { r, g, b } = rgb;
        const rgba = `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
        setIsRgba(true);
        updateElementStyles(
          selectedElement.id,
          "backgroundColor",
          rgba,
          null,
          selectedDevice
        );
      } else {
        updateElementStyles(
          selectedElement.id,
          "backgroundColor",
          color,
          null,
          selectedDevice
        );
        setIsRgba(false);
      }
    }
  };

  const handleBackgroundClipChange = (value) => {
    setBackgroundClip(value);
    
    const cssValue = {
      'none': 'border-box',
      'Clip background to padding': 'padding-box',
      'Clip background to content': 'content-box',
      'Clip background to text': 'text'
    }[value];

    updateElementStyles(
      selectedElement.id,
      "backgroundClip",
      cssValue,
      null,
      selectedDevice
    );
  };

  const handleTypeToggle = (type, e) => {
    e.stopPropagation();
    
    if (!appliedTypes[type]) {
      const currentBackground = selectedElement.styles?.[selectedDevice]?.backgroundImage || '';
      let newBackgroundValue = '';

      if (type === 'linear') {
        const defaultLinear = defaultTypeValues.linear;
        newBackgroundValue = `linear-gradient(${defaultLinear.angle}deg, ${
          defaultLinear.colors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
        })`;
        
        setGradientAngle(defaultLinear.angle);
        setGradientColors(defaultLinear.colors);
        
        if (currentBackground && currentBackground !== 'none') {
          newBackgroundValue = `${currentBackground}, ${newBackgroundValue}`;
        }

        updateElementStyles(
          selectedElement.id,
          "backgroundImage",
          newBackgroundValue,
          null,
          selectedDevice
        );
      } else if (type === 'radial') {
        const defaultRadial = defaultTypeValues.radial;
        newBackgroundValue = `radial-gradient(${defaultRadial.shape} ${defaultRadial.size} at ${defaultRadial.position}, ${
          defaultRadial.colors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
        })`;
        
        setRadialGradientShape(defaultRadial.shape);
        setRadialGradientSize(defaultRadial.size);
        setRadialGradientPosition(defaultRadial.position);
        setRadialGradientColors(defaultRadial.colors);
        
        if (currentBackground && currentBackground !== 'none') {
          newBackgroundValue = `${currentBackground}, ${newBackgroundValue}`;
        }

        updateElementStyles(
          selectedElement.id,
          "backgroundImage",
          newBackgroundValue,
          null,
          selectedDevice
        );
      } else if (type === 'image') {
        newBackgroundValue = `url(${selectedImage.url})`;

        if (currentBackground && currentBackground !== 'none') {
          newBackgroundValue = `${currentBackground}, ${newBackgroundValue}`;
        }

        console.log(newBackgroundValue);

        updateElementStyles(
          selectedElement.id,
          "backgroundImage",
          newBackgroundValue,
          null,
          selectedDevice
        );

        setBackgroundSize(defaultTypeValues.image.backgroundSize);
        setBackgroundPosition(defaultTypeValues.image.backgroundPosition);
        setBackgroundRepeat(defaultTypeValues.image.backgroundRepeat);
      }

      setAppliedTypes(prev => ({
        ...prev,
        [type]: true
      }));
      setExpandedTypes(prev => ({
        ...prev,
        [type]: true
      }));
    } else {
      const currentBackground = selectedElement.styles?.[selectedDevice]?.backgroundImage || '';
      let newBackgroundValue = currentBackground;

      if (type === 'linear') {
        newBackgroundValue = currentBackground.replace(/,?\s*linear-gradient\([^)]+\)/g, '');
      } else if (type === 'radial') {
        newBackgroundValue = currentBackground.replace(/,?\s*radial-gradient\([^)]+\)/g, '');
      } else if (type === 'image') {
        newBackgroundValue = currentBackground.replace(/,?\s*url\([^)]+\)/g, '');
      }

      if (!newBackgroundValue || newBackgroundValue === '') {
        newBackgroundValue = 'none';
      }

      updateElementStyles(
        selectedElement.id,
        "backgroundImage",
        newBackgroundValue,
        null,
        selectedDevice
      );

      setAppliedTypes(prev => ({
        ...prev,
        [type]: false
      }));
      setExpandedTypes(prev => ({
        ...prev,
        [type]: false
      }));
    }
  };

  const handleBackgroundPositionChange = (position, value) => {
    const newPosition = {
      ...backgroundPosition,
      [position]: { ...backgroundPosition[position], value }
    };
    setBackgroundPosition(newPosition);

    const positionValue = `${newPosition.left.value}% ${newPosition.top.value}%`;
    
    updateElementStyles(
      selectedElement.id,
      "backgroundPosition",
      positionValue,
      null,
      selectedDevice
    );
  };

  const handleBackgroundRepeatChange = (value) => {
    updateElementStyles(
      selectedElement.id,
      "backgroundRepeat",
      value,
      null,
      selectedDevice
    );
  };

  const handleGradientUpdate = useCallback(
    debounce((angle, colors, elementId, device) => {
      if (!elementId) return;

      const gradientValue = `linear-gradient(${angle}deg, ${
        colors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
      })`;

      updateElementStyles(
        elementId,
        "backgroundImage",
        gradientValue,
        null,
        device
      );
    }, 16),
    [selectedElement, classStylesRegistry, updateElementStyles]
  );

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const handleColorStopChange = (index, color) => {
    const newColors = [...gradientColors];
    newColors[index] = { ...newColors[index], color };
    setGradientColors(newColors);
    handleGradientUpdate(gradientAngle, newColors, selectedElement?.id, selectedDevice);
  };

  const handlePositionChange = (index, position) => {
    const newColors = [...gradientColors];
    newColors[index] = { ...newColors[index], position: Math.max(0, Math.min(100, position)) };
    setGradientColors(newColors);
    handleGradientUpdate(gradientAngle, newColors, selectedElement?.id, selectedDevice);
  };

  const handleAngleChange = (angle) => {
    setGradientAngle(angle);
    
    const previewGradient = `linear-gradient(${angle}deg, ${
      gradientColors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
    })`;
    
    handleGradientUpdate(angle, gradientColors, selectedElement?.id, selectedDevice);
  };

  const handleRadialGradientUpdate = useCallback(
    debounce((shape, position, size, colors, elementId, device) => {
      if (!elementId) return;

      const gradientValue = `radial-gradient(${shape} ${size} at ${position}, ${
        colors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
      })`;

      updateElementStyles(
        elementId,
        "backgroundImage",
        gradientValue,
        null,
        device
      );
    }, 16),
    [selectedElement, classStylesRegistry, updateElementStyles]
  );

  const handleRadialColorChange = (index, color) => {
    const newColors = [...radialGradientColors];
    newColors[index] = { ...newColors[index], color };
    setRadialGradientColors(newColors);
    handleRadialGradientUpdate(
      radialGradientShape,
      radialGradientPosition,
      radialGradientSize,
      newColors,
      selectedElement?.id,
      selectedDevice
    );
  };

  const handleRadialPositionChange = (index, position) => {
    const newColors = [...radialGradientColors];
    newColors[index] = { ...newColors[index], position: Math.max(0, Math.min(100, position)) };
    setRadialGradientColors(newColors);
    handleRadialGradientUpdate(
      radialGradientShape,
      radialGradientPosition,
      radialGradientSize,
      newColors,
      selectedElement?.id,
      selectedDevice
    );
  };

  const handleAssetSelect = (asset) => {
    if (!asset || !asset.url) {
      console.error("Invalid asset selected:", asset);
      return;
    }
    
    setSelectedImage({
      url: asset.url,
      name: asset.fileName || "background-image.png"
    });
    
    setIsAssetPickerOpen(false);
    
    const currentBackground = selectedElement.styles?.[selectedDevice]?.backgroundImage || '';
    let newBackgroundValue;

    const imageBackgroundValue = `url('${asset.url}')`;
    
    if (!appliedTypes.image) {
      setAppliedTypes(prev => ({
        ...prev,
        image: true
      }));
      
      setExpandedTypes(prev => ({
        ...prev,
        image: true
      }));
    }
    
    if (currentBackground && currentBackground.includes('url(')) {
      newBackgroundValue = currentBackground.replace(/url\([^)]+\)/, imageBackgroundValue);
    } else if (currentBackground && currentBackground !== 'none') {
      newBackgroundValue = `${imageBackgroundValue}, ${currentBackground}`;
    } else {
      newBackgroundValue = imageBackgroundValue;
    }
    
    updateElementStyles(
      selectedElement.id,
      "backgroundImage",
      newBackgroundValue,
      null,
      selectedDevice
    );
  };

  return (
    <div className="background-section">
      <div className="background-property">
        <span className="background-label">Bg Color</span>
        <div className="background-color-group">
          <div className="color-input-wrapper">
            <input
              type="color"
              value={isRgba ? rgbaToHex(backgroundColor) : backgroundColor}
              onChange={(e) => handleBackgroundColorChange(e.target.value)}
            />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* <input
              type="text"
              value={backgroundColor}
              onChange={(e) => {
                const newColor = e.target.value;
                if (newColor.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                  setBackgroundColor(newColor);
                  if (newColor.match(/^#[0-9A-Fa-f]{6}$/)) {
                    handleBackgroundColorChange(newColor);
                  }
                }
              }}
              onFocus={() => setIsColorInputFocused(true)}
              onBlur={() => setIsColorInputFocused(false)}
              className={isColorInputFocused ? "focused" : ""}
              placeholder="#000000"
            /> */}
            <div className="alpha-control">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={colorAlpha}
                onChange={handleAlphaChange}
                className="alpha-slider compact"
                style={{
                  background: `linear-gradient(to right, rgba(0, 0, 0, 0), ${
                    isRgba ? rgbaToHex(backgroundColor) : backgroundColor
                  })`
                }}
              />
              <span className="alpha-badge">{Math.round(colorAlpha * 100)}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="background-property">
        <span className="background-label">Clipping</span>
        <div className="background-clip-dropdown">
          <button
            className="clip-button"
            onClick={() => setIsClipDropdownOpen(!isClipDropdownOpen)}
          >
            {backgroundClip === "border-box" ? "None" :
             backgroundClip === "padding-box" ? "Clip background to padding" :
             backgroundClip === "content-box" ? "Clip background to content" :
             backgroundClip === "text" ? "Clip background to text" : "None"}
            <span className="dropdown-arrow">
              {isClipDropdownOpen ? "▼" : "▶"}
            </span>
          </button>
          {isClipDropdownOpen && (
            <div className="clip-dropdown-content">
              {[
                "None",
                "Clip background to padding",
                "Clip background to content",
                "Clip background to text"
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    handleBackgroundClipChange(option);
                    setIsClipDropdownOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={`background-property background-section-header ${isImageGradientExpanded ? 'expanded' : ''}`}>
        <span className="background-image-label">Background Image and Gradient</span>
        <div className="image-gradient-wrapper">
          <button 
            className={`add-image-gradient-button ${isImageGradientExpanded ? 'active' : ''}`}
            onClick={() => setIsImageGradientExpanded(!isImageGradientExpanded)}
            title="Toggle Image and Gradient"
          >
            <span>{isImageGradientExpanded ? 'Close' : 'Open'}</span>
            <i className={`fas ${isImageGradientExpanded ? 'fa-minus' : 'fa-plus'}`} style={{ color: "#fff" }}></i>
          </button>
        </div>
      </div>

      {isImageGradientExpanded && (
        <div className="background-types">
          <div className="background-type-section">
            <div className="type-header">
              <div className="type-title">
                <i className="fas fa-image"></i>
                <span>Background Image</span>
              </div>
              <div className="type-controls">
                <button
                  className={`type-toggle-button ${appliedTypes.image ? 'remove' : 'apply'}`}
                  onClick={(e) => handleTypeToggle('image', e)}
                >
                  {appliedTypes.image ? 'Remove' : 'Apply'}
                </button>
                {appliedTypes.image && (
                  <i 
                    className={`fas ${expandedTypes.image ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedTypes(prev => ({...prev, image: !prev.image}))
                    }}
                  ></i>
                )}
              </div>
            </div>
            
            {appliedTypes.image && expandedTypes.image && (
              <div className="type-content">
                <div className="image-section">
                  <div className="section-title">Image</div>
                  <div className="image-preview-container">
                    <div className="image-preview-wrapper">
                      <img 
                        src={selectedImage.url} 
                        alt="Background Preview" 
                        className="preview-image"
                      />
                      <button 
                        className="change-image-btn"
                        onClick={() => setIsAssetPickerOpen(true)}
                      >
                        <i className="fas fa-image"></i>
                        <span>Choose Image</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="image-section" style={{ marginTop: "12px" }}>
                  <div className="section-title">Size</div>
                  <div className="size-type-buttons">
                    <button
                      className={`size-type-button ${backgroundSize === "custom" ? "active" : ""}`}
                      onClick={() => {
                        setBackgroundSize("custom");
                        if (appliedTypes.image) {
                          const sizeValue = `${customSize.width.value}${customSize.width.unit} ${customSize.height.value}${customSize.height.unit}`;
                          updateElementStyles(
                            selectedElement.id,
                            "backgroundSize",
                            sizeValue,
                            null,
                            selectedDevice
                          );
                        }
                      }}

                    >
                      Custom
                    </button>
                    <button
                      className={`size-type-button ${backgroundSize === "cover" ? "active" : ""}`}
                      onClick={() => {
                        setBackgroundSize("cover");
                        if (appliedTypes.image) {
                          updateElementStyles(
                            selectedElement.id,
                            "backgroundSize",
                            "cover",
                            null,
                            selectedDevice
                          );
                        }
                      }}

                    >
                      Cover
                    </button>
                    <button
                      className={`size-type-button ${backgroundSize === "contain" ? "active" : ""}`}
                      onClick={() => {
                        setBackgroundSize("contain");
                        if (appliedTypes.image) {
                          updateElementStyles(
                            selectedElement.id,
                            "backgroundSize",
                            "contain",
                            null,
                            selectedDevice
                          );
                        }
                      }}

                    >
                      Contain
                    </button>
                  </div>
                </div>
                
                <div className="image-section" style={{ marginTop: "12px" }}>
                  <div className="section-title">Position</div>
                  <div className="background-position-box">
                    <div className="position-grid">
                      {[
                        'top left', 'top center', 'top right',
                        'center left', 'center', 'center right',
                        'bottom left', 'bottom center', 'bottom right'
                      ].map(pos => {
                        let leftValue, topValue;
                        
                        if (pos === 'center') {
                          leftValue = 50;
                          topValue = 50;
                        } else {
                          const parts = pos.split(' ');
                          const vertical = parts[0];
                          const horizontal = parts[1];

                          topValue = vertical === 'top' ? 0 : vertical === 'bottom' ? 100 : 50;

                          if (horizontal) {
                            leftValue = horizontal === 'left' ? 0 : horizontal === 'right' ? 100 : 50;
                          } else {
                            leftValue = 50;
                          }
                        }
                        
                        const isActive = parseInt(backgroundPosition.left.value) === leftValue && 
                                        parseInt(backgroundPosition.top.value) === topValue;

                        return (
                          <button
                            key={pos}
                            className={`position-dot ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              const newPosition = {
                                left: { ...backgroundPosition.left, value: leftValue },
                                top: { ...backgroundPosition.top, value: topValue }
                              };
                              setBackgroundPosition(newPosition);

                              const positionValue = `${leftValue}% ${topValue}%`;
                              updateElementStyles(
                                selectedElement.id,
                                "backgroundPosition",
                                positionValue,
                                null,
                                selectedDevice
                              );
                            }}
                          />
                        );
                      })}
                    </div>

                    <div className="position-inputs">
                      <div className="position-input-wrapper">
                        <span className="position-label">Left</span>
                        <div className="position-input-group">
                          <input
                            type="text"
                            value={backgroundPosition.left.value}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*$/.test(value)) {
                                handleBackgroundPositionChange('left', value);
                              }
                            }}
                            placeholder="50"
                          />
                          <div className="position-unit">%</div>
                        </div>
                      </div>

                      <div className="position-input-wrapper">
                        <span className="position-label">Top</span>
                        <div className="position-input-group">
                          <input
                            type="text"
                            value={backgroundPosition.top.value}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*$/.test(value)) {
                                handleBackgroundPositionChange('top', value);
                              }
                            }}
                            placeholder="50"
                          />
                          <div className="position-unit">%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="image-section" style={{ marginTop: "12px" }}>
                  <div className="section-title">Tile</div>
                  <div className="background-tile-box">
                    <div className="tile-buttons">
                      <button
                        className={`tile-button ${backgroundRepeat === "repeat" ? "active" : ""}`}
                        onClick={() => handleBackgroundRepeatChange("repeat")}
                      >
                        <i className="fas fa-border-all"></i>
                        <span>Repeat</span>
                      </button>
                      <button
                        className={`tile-button ${backgroundRepeat === "repeat-x" ? "active" : ""}`}
                        onClick={() => handleBackgroundRepeatChange("repeat-x")}
                      >
                        <i className="fas fa-grip-lines"></i>
                        <span>Repeat-X</span>
                      </button>
                      <button
                        className={`tile-button ${backgroundRepeat === "repeat-y" ? "active" : ""}`}
                        onClick={() => handleBackgroundRepeatChange("repeat-y")}
                      >
                        <i className="fas fa-grip-lines-vertical"></i>
                        <span>Repeat-Y</span>
                      </button>
                      <button
                        className={`tile-button ${backgroundRepeat === "no-repeat" ? "active" : ""}`}
                        onClick={() => handleBackgroundRepeatChange("no-repeat")}
                      >
                        <i className="fas fa-square"></i>
                        <span>No-Repeat</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="background-type-section">
            <div className="type-header">
              <div className="type-title">
                <i className="fas fa-grip-lines"></i>
                <span>Linear Gradient</span>
              </div>
              <div className="type-controls">
                <button
                  className={`type-toggle-button ${appliedTypes.linear ? 'remove' : 'apply'}`}
                  onClick={(e) => handleTypeToggle('linear', e)}
                >
                  {appliedTypes.linear ? 'Remove' : 'Apply'}
                </button>
                {appliedTypes.linear && (
                  <i 
                    className={`fas ${expandedTypes.linear ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedTypes(prev => ({...prev, linear: !prev.linear}))
                    }}
                  ></i>
                )}
              </div>
            </div>
            
            {appliedTypes.linear && expandedTypes.linear && (
              <div className="type-content">
                <div className="gradient-section">
                  <div className="section-title">Colors</div>
                  <div className="gradient-preview" 
                    style={{
                      background: `linear-gradient(${gradientAngle}deg, ${
                        gradientColors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
                      })`
                    }}
                  />
                  
                  <div className="color-stops">
                    {gradientColors.map((stop, index) => (
                      <div key={index} className="color-stop">
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={stop.color}
                            onChange={(e) => handleColorStopChange(index, e.target.value)}
                          />
                        </div>
                        <input
                          type="number"
                          value={stop.position}
                          onChange={(e) => handlePositionChange(index, parseInt(e.target.value))}
                          min="0"
                          max="100"
                        />
                        <span className="position-unit">%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="gradient-section" style={{ marginTop: "12px" }}>
                  <div className="section-title">Angle</div>
                  <div className="angle-control">
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={gradientAngle}
                      onChange={(e) => handleAngleChange(parseInt(e.target.value))}
                      style={{
                        background: `linear-gradient(to right, #444 0%, #666 50%, #444 100%)`,
                        WebkitAppearance: 'none'
                      }}
                    />
                    <div className="angle-input">
                      <input
                        type="number"
                        value={gradientAngle}
                        onChange={(e) => {
                          const value = Math.min(360, Math.max(0, parseInt(e.target.value) || 0));
                          handleAngleChange(value);
                        }}
                        min="0"
                        max="360"
                      />
                      <span className="angle-unit">°</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="background-type-section">
            <div className="type-header">
              <div className="type-title">
                <i className="fas fa-circle"></i>
                <span>Radial Gradient</span>
              </div>
              <div className="type-controls">
                <button
                  className={`type-toggle-button ${appliedTypes.radial ? 'remove' : 'apply'}`}
                  onClick={(e) => handleTypeToggle('radial', e)}
                >
                  {appliedTypes.radial ? 'Remove' : 'Apply'}
                </button>
                {appliedTypes.radial && (
                  <i 
                    className={`fas ${expandedTypes.radial ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedTypes(prev => ({...prev, radial: !prev.radial}))
                    }}
                  ></i>
                )}
              </div>
            </div>
            
            {appliedTypes.radial && expandedTypes.radial && (
              <div className="type-content">
                <div className="gradient-section">
                  <div className="section-title">Shape</div>
                  <div className="radial-controls">
                    <button
                      className={`radial-button ${radialGradientShape === 'circle' ? 'active' : ''}`}
                      onClick={() => {
                        setRadialGradientShape('circle');
                        handleRadialGradientUpdate(
                          'circle',
                          radialGradientPosition,
                          radialGradientSize,
                          radialGradientColors,
                          selectedElement?.id,
                          selectedDevice
                        );
                      }}
                    >
                      <i className="fas fa-circle"></i>
                      <span>Circle</span>
                    </button>
                    <button
                      className={`radial-button ${radialGradientShape === 'ellipse' ? 'active' : ''}`}
                      onClick={() => {
                        setRadialGradientShape('ellipse');
                        handleRadialGradientUpdate(
                          'ellipse',
                          radialGradientPosition,
                          radialGradientSize,
                          radialGradientColors,
                          selectedElement?.id,
                          selectedDevice
                        );
                      }}
                    >
                      <i className="fas fa-ellipse-alt"></i>
                      <span>Ellipse</span>
                    </button>
                  </div>
                </div>

                <div className="section-title" style={{ marginTop: "12px" }}>Position</div>
                <div className="position-grid">
                  {[
                    'top left', 'top center', 'top right',
                    'center left', 'center', 'center right',
                    'bottom left', 'bottom center', 'bottom right'
                  ].map(pos => (
                    <button
                      key={pos}
                      className={`position-dot ${radialGradientPosition === pos ? 'active' : ''}`}
                      onClick={() => {
                        setRadialGradientPosition(pos);
                        handleRadialGradientUpdate(
                          radialGradientShape,
                          pos,
                          radialGradientSize,
                          radialGradientColors,
                          selectedElement?.id,
                          selectedDevice
                        );
                      }}
                    />
                  ))}
                </div>

                <div className="section-title" style={{ marginTop: "12px" }}>Size</div>
                <div className="size-buttons">
                  {[
                    'closest-side',
                    'closest-corner',
                    'farthest-side',
                    'farthest-corner'
                  ].map(size => (
                    <button
                      key={size}
                      className={`size-button ${radialGradientSize === size ? 'active' : ''}`}
                      onClick={() => {
                        setRadialGradientSize(size);
                        handleRadialGradientUpdate(
                          radialGradientShape,
                          radialGradientPosition,
                          size,
                          radialGradientColors,
                          selectedElement?.id,
                          selectedDevice
                        );
                      }}
                    >
                      {size.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </button>
                  ))}
                </div>

                <div className="section-title" style={{ marginTop: "12px" }}>Colors</div>
                <div className="gradient-preview" 
                  style={{
                    background: `radial-gradient(${radialGradientShape} ${radialGradientSize} at ${radialGradientPosition}, ${
                      radialGradientColors.map(stop => `${stop.color} ${stop.position}%`).join(', ')
                    })`
                  }}
                />
                
                <div className="color-stops">
                  {radialGradientColors.map((stop, index) => (
                    <div key={index} className="color-stop">
                      <div className="color-input-wrapper">
                        <input
                          type="color"
                          value={stop.color}
                          onChange={(e) => handleRadialColorChange(index, e.target.value)}
                        />
                      </div>
                      <input
                        type="number"
                        value={stop.position}
                        onChange={(e) => handleRadialPositionChange(index, parseInt(e.target.value))}
                        min="0"
                        max="100"
                      />
                      <span className="position-unit">%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {isAssetPickerOpen && (
        <AssetPickerPopup 
          assets={assets}
          onClose={() => setIsAssetPickerOpen(false)}
          onSelectAsset={handleAssetSelect}
        />
      )}
    </div>
  );
};

export default Background;  